import React from 'react';
import styles from './SelectionChip.module.css';

/**
 * SelectionChip — read-only Selection decision badge for the Developments portal
 * (BRIEF 7). Display-only — never writes, never reads the selection_* tables.
 *
 * Task 3 (chip refresh): the headline is ALWAYS the decision —
 *   in | order | preorder → green "IN <season>"   (in the selection)
 *   out                    → red   "OUT <season>"  (rejected)
 * The procurement sub-status (ORDER / PRE-ORDER) is revealed on HOVER beside the
 * headline (amber slide-out), so a committed dev reads "IN 2027" at rest and
 * "IN 2027 · ORDER" on hover. A plain IN → just the headline, nothing on hover.
 *
 * Self-contained palette (no ancestor design tokens) per Convention #142 — renders
 * identically on the dev card (.cardRoot) and the V2 modal (.devModalRoot).
 *
 * Season rule (DISPLAY): newest stamped key — max(Object.keys(selections)) numeric.
 * Missing / empty / unrecognized → renders nothing (safe for the ~3,700 undecided).
 */

// Task 4 / dev-procurement-sync — SELECTION_SEASON (the FIXED season every filter
// targets) and readSelectionAt (the one tolerant reader of a season object, legacy
// encoding included) live in ./selectionSeason.js, a pure module, and are
// re-exported here so every existing import path keeps working (Collection C2).
import { SELECTION_SEASON, readSelectionAt } from './selectionSeason';
export { SELECTION_SEASON, readSelectionAt };

// Returns { season, decision, procurement } for the newest stamped season, or
// null. `decision` is only ever 'in' | 'out' | null; `procurement` only ever
// 'order' | 'preorder' | null. A dev may carry procurement with NO decision —
// the synchroniser can mark a model that the meeting never discussed.
//
// The Array.isArray refusal is load-bearing, not defensive noise. Between
// 2026-07-27 and 08-07 the Apply stamp wrote 1,007 developments as an ARRAY
// (`[null, {"2027": {...}}]`) instead of the season-keyed object, and the board
// showed nothing for every one of them. That data is repaired
// (selections-shape-normalise-20260807) but `typeof [] === 'object'`, so an
// array still reaches the season pick below — and the pick is
// max(Object.keys()), which on an array selects a numeric INDEX. Measured: the
// production shape returns null only because the object sat at index 1 with no
// own `decision` key, i.e. by luck. `[null, {decision:'out'}]` resolves to
// {season:'1', decision:'out'} and renders "OUT 1" — a wrong decision under a
// nonsense season. This makes the refusal explicit so a malformed value can
// never be read as a decision. The two other readers (useDevFilters.js,
// factoryBits.decisionOf) index the literal season key and are safe already.
export function resolveSelectionDecision(selections) {
  if (!selections || typeof selections !== 'object' || Array.isArray(selections)) return null;
  const keys = Object.keys(selections);
  if (!keys.length) return null;
  const season = keys.slice().sort((a, b) => (Number(b) || 0) - (Number(a) || 0))[0];
  const r = readSelectionAt(selections[season]);
  if (!r) return null;
  return { season, decision: r.decision, procurement: r.procurement };
}

// Label for the procurement axis. One place, so the chip and the grid pill can
// never drift apart on wording.
export function procurementLabel(p) {
  return p === 'order' ? 'ORDER' : p === 'preorder' ? 'PRE-ORDER' : null;
}

export default function SelectionChip({ selections }) {
  const r = resolveSelectionDecision(selections);
  if (!r) return null;
  const sub = procurementLabel(r.procurement);
  const subCls = r.procurement === 'order' ? styles.subOrder : styles.subPre;

  // Ordered, but the meeting never decided it. Show the procurement alone rather
  // than inventing an IN that nobody gave it. The sub-pill is hover-revealed, so
  // it becomes the always-visible core here instead.
  if (!r.decision) {
    return (
      <span className={`${styles.chip} ${styles.chipProc} ${subCls}`}>
        <span className={styles.core}>{sub}</span>
      </span>
    );
  }

  const isIn = r.decision !== 'out';
  return (
    <span className={`${styles.chip} ${isIn ? styles.chipIn : styles.chipOut}`}>
      <span className={styles.core}>{isIn ? 'IN' : 'OUT'} {r.season}</span>
      {sub && <span className={`${styles.sub} ${subCls}`}>{sub}</span>}
    </span>
  );
}
